import { Link } from 'react-router-dom';
import type { OutboxState } from '../../lib/offline/useOutbox.js';
import { t } from '../../lib/i18n.js';

/**
 * Connectivity and queue state, shown on every App 2 screen.
 *
 * It says nothing at all when there is a network and nothing queued, because a
 * banner that is always there is a banner nobody reads. It appears when the
 * answer to "has my work left this phone" is anything other than yes.
 *
 * ADR-0014: logical properties throughout, so this mirrors under dir="rtl".
 */
export function OfflineBanner({ state }: { state: OutboxState }) {
  const { online, pending, stuck, flushing, lastReport } = state;

  // Quiet when there is nothing to say — but a sync that just finished, or
  // work waiting to be reconciled, is something to say (pass 9: both were hidden).
  const justSynced = Boolean(lastReport && lastReport.attempted > 0 && lastReport.failed === 0);
  const toReconcile = lastReport?.conflicted ?? 0;
  if (online && pending === 0 && stuck.length === 0 && !justSynced && toReconcile === 0) return null;

  const tone = stuck.length > 0 ? 'rejected' : online ? 'pending' : 'draft';

  return (
    <div
      role="status"
      className="border-b border-border px-4 py-2 text-sm"
      style={{
        background: `var(--tone-${tone}-surface)`,
        borderInlineStartWidth: '4px',
        borderInlineStartColor: `var(--tone-${tone})`,
      }}
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-1">
        {!online && <strong>{t("Working offline.")}</strong>}

        {pending > 0 && (
          <span>
            {pending} {pending === 1 ? t("change is") : t("changes are")}{' '}{t("waiting on this device")}
            {online
              ? t(" and sending now.")
              : t(" — they send when you have a signal and QMS360 is open. Keep it open, or open it again later.")}
          </span>
        )}

        {online && pending === 0 && stuck.length === 0 && justSynced && <span>{t("All changes are synced.")}</span>}

        {stuck.length > 0 && (
          <div>
            {stuck.length} {stuck.length === 1 ? t("change") : t("changes")}{' '}{t("the server would not accept. Nothing has been discarded; correct the lesson and it is sent again.")}
            <details className="mt-1">
              <summary className="cursor-pointer font-medium">{t("Why")}</summary>
              <ul className="mb-0 mt-1 ps-5">
                {stuck.map((entry) => (
                  <li key={entry.id}>{entry.error}</li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {lastReport && lastReport.conflicted > 0 && (
          <Link to="/lesson-learned/conflicts" className="font-medium underline">
            {lastReport.conflicted}{' '}{t("need reconciling")}
          </Link>
        )}

        {online && pending > 0 && (
          <button
            type="button"
            onClick={() => void state.flushNow()}
            disabled={flushing}
            className="ms-auto rounded-sm border border-border-strong bg-surface px-3 py-1 text-sm font-medium disabled:opacity-60"
          >
            {flushing ? t("Syncing…") : t("Sync now")}
          </button>
        )}
      </div>
    </div>
  );
}
